import React, { useState, useEffect, useCallback } from 'react';
import CaroBoard from './CaroBoard';
import useMobileDetection from '../hooks/useMobileDetection';
import { checkCaroWin, getGameStatus, isBoardFull } from '../utils/caroWinChecker';

const SinglePlayerGame = ({ onNavigate }) => {
  const [gameState, setGameState] = useState(null);
  const [currentTurn, setCurrentTurn] = useState('X');
  const [gameStatus, setGameStatus] = useState({ isGameOver: false, winner: null, winningLine: null });
  const [lastMove, setLastMove] = useState(null); // Track the last move {row, col}
  const [playerColor, setPlayerColor] = useState('X');
  const [aiColor, setAiColor] = useState('O');
  const [boardSize, setBoardSize] = useState(15);
  const [winCondition, setWinCondition] = useState(5);
  const [difficulty, setDifficulty] = useState('medium');
  const [gameStarted, setGameStarted] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const { isMobile } = useMobileDetection();

  const [gameStats, setGameStats] = useState({
    gamesPlayed: 0,
    wins: 0,
    losses: 0,
    draws: 0
  });

  // Load stats from localStorage
  useEffect(() => {
    const savedStats = localStorage.getItem('caro-single-player-stats');
    if (savedStats) {
      setGameStats(JSON.parse(savedStats));
    }
  }, []);

  // Save stats to localStorage
  const saveStats = useCallback((newStats) => {
    localStorage.setItem('caro-single-player-stats', JSON.stringify(newStats));
    setGameStats(newStats);
  }, []);

  // Initialize empty board
  const initializeBoard = useCallback(() => {
    const newBoard = Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
    setGameState(newBoard);
    setCurrentTurn('X');
    setGameStatus({ isGameOver: false, winner: null, winningLine: null });
    setLastMove(null); // Reset last move
  }, [boardSize]);

  useEffect(() => {
    if (gameStarted) {
      initializeBoard();
    }
  }, [gameStarted, initializeBoard]);

  // Check for winning condition using standardized logic
  const checkWinner = useCallback((board, lastRow, lastCol, player) => {
    return checkCaroWin(board, lastRow, lastCol, player, winCondition);
  }, [winCondition]);

  // Check if board is full - using standardized logic
  // (Removed local implementation, using imported function)

  // Enhanced AI Logic with pattern recognition
  const evaluatePosition = useCallback((board, row, col, player) => {
    const opponent = player === 'X' ? 'O' : 'X';
    let score = 0;

    const directions = [
      [0, 1], [1, 0], [1, 1], [1, -1]
    ];

    for (const [dx, dy] of directions) {
      let count = 1;
      let openEnds = 0;
      let blocked = 0;
      
      // Check both directions
      for (const direction of [-1, 1]) {
        let r = row + dx * direction;
        let c = col + dy * direction;
        let consecutiveCount = 0;
        
        // Count consecutive pieces
        while (r >= 0 && r < boardSize && c >= 0 && c < boardSize && board[r][c] === player) {
          consecutiveCount++;
          r += dx * direction;
          c += dy * direction;
        }
        
        count += consecutiveCount;
        
        // Check if this end is open or blocked
        if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
          if (board[r][c] === null) {
            openEnds++;
          } else if (board[r][c] === opponent) {
            blocked++;
          }
        } else {
          blocked++; // Edge of board counts as blocked
        }
      }

      // Advanced scoring based on patterns
      if (count >= winCondition) {
        score += 100000; // Instant win
      } else if (count >= winCondition - 1) {
        if (openEnds >= 1) {
          score += 10000; // Can win next turn
        } else {
          score += 1000; // Blocked but still threatening
        }
      } else if (count >= winCondition - 2) {
        if (openEnds >= 2) {
          score += 1000; // Open three/four
        } else if (openEnds === 1) {
          score += 100; // Semi-open three/four
        }
      } else if (count >= 2) {
        if (openEnds >= 2) {
          score += 50; // Open two
        } else if (openEnds === 1) {
          score += 10; // Semi-open two
        }
      }

      // Penalty for being blocked
      if (blocked >= 2) {
        score -= count * 5;
      }
    }

    // Center preference (stronger in early game)
    const centerRow = Math.floor(boardSize / 2);
    const centerCol = Math.floor(boardSize / 2);
    const distanceFromCenter = Math.abs(row - centerRow) + Math.abs(col - centerCol);
    score += Math.max(0, (boardSize / 2 - distanceFromCenter)) * 3;

    return score;
  }, [boardSize, winCondition]);

  // Strategic value evaluation
  const evaluateStrategicValue = useCallback((board, row, col) => {
    let strategicValue = 0;

    // Center preference (stronger in early game)
    const centerRow = Math.floor(boardSize / 2);
    const centerCol = Math.floor(boardSize / 2);
    const distanceFromCenter = Math.abs(row - centerRow) + Math.abs(col - centerCol);
    const moveCount = board.flat().filter(cell => cell !== null).length;
    
    if (moveCount < 10) { // Early game
      strategicValue += (boardSize - distanceFromCenter) * 5;
    }

    // Prefer moves near existing pieces
    for (let dr = -2; dr <= 2; dr++) {
      for (let dc = -2; dc <= 2; dc++) {
        const nr = row + dr;
        const nc = col + dc;
        if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && board[nr][nc] !== null) {
          strategicValue += Math.max(0, 3 - Math.abs(dr) - Math.abs(dc));
        }
      }
    }

    return strategicValue;
  }, [boardSize]);

  const getAIMove = useCallback((board) => {
    const availableMoves = [];
    
    // Find all available moves
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (board[row][col] === null) {
          availableMoves.push([row, col]);
        }
      }
    }

    if (availableMoves.length === 0) return null;

    // Easy difficulty - random move with slight preference for center
    if (difficulty === 'easy') {
      if (Math.random() < 0.7) {
        return availableMoves[Math.floor(Math.random() * availableMoves.length)];
      }
    }

    // Medium/Hard difficulty - evaluate moves
    let bestMoves = [];
    let bestScore = -Infinity;

    for (const [row, col] of availableMoves) {
      let score = 0;

      // Prioritize winning moves
      const aiScore = evaluatePosition(board, row, col, aiColor);
      score += aiScore;

      // Prioritize blocking opponent wins
      const playerScore = evaluatePosition(board, row, col, playerColor);
      score += playerScore * 0.95; // Slightly less than winning

      // Add strategic positioning
      score += evaluateStrategicValue(board, row, col);

      // Add randomness for medium difficulty
      if (difficulty === 'medium') {
        score += Math.random() * 100;
      }

      if (score > bestScore) {
        bestScore = score;
        bestMoves = [[row, col]];
      } else if (score === bestScore) {
        bestMoves.push([row, col]);
      }
    }

    // Return random move from best moves
    return bestMoves[Math.floor(Math.random() * bestMoves.length)];
  }, [boardSize, difficulty, aiColor, playerColor, evaluatePosition, evaluateStrategicValue]);

  // Make move
  const makeMove = useCallback((row, col) => {
    if (!gameState || gameState[row][col] !== null || gameStatus.isGameOver) {
      return;
    }

    const newBoard = gameState.map(r => [...r]);
    newBoard[row][col] = currentTurn;
    setGameState(newBoard);
    setLastMove({ row, col }); // Track the last move

    // Check for winner
    const winResult = checkWinner(newBoard, row, col, currentTurn);
    if (winResult) {
      setGameStatus({
        isGameOver: true,
        winner: winResult.winner,
        winningLine: winResult.winningLine
      });

      // Update game stats
      if (winResult.winner === playerColor) {
        saveStats({
          ...gameStats,
          wins: gameStats.wins + 1,
          gamesPlayed: gameStats.gamesPlayed + 1
        });
      } else if (winResult.winner === aiColor) {
        saveStats({
          ...gameStats,
          losses: gameStats.losses + 1,
          gamesPlayed: gameStats.gamesPlayed + 1
        });
      } else {
        saveStats({
          ...gameStats,
          draws: gameStats.draws + 1,
          gamesPlayed: gameStats.gamesPlayed + 1
        });
      }

      return;
    }

    // Check for draw
    if (isBoardFull(newBoard)) {
      setGameStatus({
        isGameOver: true,
        winner: null,
        winningLine: null
      });

      // Update game stats
      saveStats({
        ...gameStats,
        draws: gameStats.draws + 1,
        gamesPlayed: gameStats.gamesPlayed + 1
      });

      return;
    }

    // Switch turn
    setCurrentTurn(currentTurn === 'X' ? 'O' : 'X');
  }, [gameState, currentTurn, gameStatus.isGameOver, checkWinner, gameStats, saveStats, playerColor, aiColor]);

  // AI move effect
  useEffect(() => {
    if (gameState && currentTurn === aiColor && !gameStatus.isGameOver) {
      setIsAiThinking(true);
      const aiMoveDelay = difficulty === 'easy' ? 500 : difficulty === 'medium' ? 1000 : 1500;
      
      const timer = setTimeout(() => {
        const aiMove = getAIMove(gameState);
        if (aiMove) {
          makeMove(aiMove[0], aiMove[1]);
        }
        setIsAiThinking(false);
      }, aiMoveDelay);

      return () => clearTimeout(timer);
    }
  }, [gameState, currentTurn, aiColor, gameStatus.isGameOver, difficulty, getAIMove, makeMove]);

  const startGame = () => {
    setGameStarted(true);
  };

  const resetGame = () => {
    setGameStarted(false);
    setGameState(null);
    setCurrentTurn('X');
    setGameStatus({ isGameOver: false, winner: null, winningLine: null });
    setIsAiThinking(false);
  };

  const handleBackToHome = () => {
    onNavigate('home');
  };

  const resetStats = () => {
    if (window.confirm('Are you sure you want to reset your statistics?')) {
      const newStats = {
        gamesPlayed: 0,
        wins: 0,
        losses: 0,
        draws: 0
      };
      saveStats(newStats);
    }
  };

  if (!gameStarted) {
    return (
      <div className="single-player-setup">
        <div className="container">
          <div className="card">
            <h2>🤖 Single Player Game</h2>
            <p>Play Caro against AI</p>
            
            <div className="form-container">
              <div className="form-group">
                <label>Your Color:</label>
                <select 
                  value={playerColor} 
                  onChange={(e) => {
                    setPlayerColor(e.target.value);
                    setAiColor(e.target.value === 'X' ? 'O' : 'X');
                  }}
                >
                  <option value="X">X (First Player)</option>
                  <option value="O">O (Second Player)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Difficulty:</label>
                <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                  <option value="easy">🟢 Easy - Random moves with basic strategy</option>
                  <option value="medium">🟡 Medium - Smart moves with some randomness</option>
                  <option value="hard">🔴 Hard - Advanced AI strategy</option>
                </select>
              </div>

              <div className="form-group">
                <label>Board Size:</label>
                <select value={boardSize} onChange={(e) => setBoardSize(Number(e.target.value))}>
                  <option value={11}>11×11 (Small)</option>
                  <option value={13}>13×13 (Medium)</option>
                  <option value={15}>15×15 (Standard)</option>
                  <option value={19}>19×19 (Large)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Win Condition:</label>
                <select value={winCondition} onChange={(e) => setWinCondition(Number(e.target.value))}>
                  <option value={4}>4 in a row</option>
                  <option value={5}>5 in a row (Standard)</option>
                  <option value={6}>6 in a row</option>
                </select>
              </div>

              <div className="form-actions">
                <button className="primary-button" onClick={startGame}>
                  🎮 Start Game
                </button>
                <button className="secondary-button" onClick={handleBackToHome}>
                  ← Back to Home
                </button>
              </div>

              <div className="ai-tips">
                <h4>💡 Tips</h4>
                <ul>
                  <li><strong>Easy:</strong> Good for learning the game</li>
                  <li><strong>Medium:</strong> Balanced challenge for most players</li>
                  <li><strong>Hard:</strong> Advanced AI for experienced players</li>
                  <li>Your statistics are saved locally</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="single-player-game">
      <div className="game-container">
        {isMobile && (
          <div className="mobile-game-nav">
            <div className="mobile-nav-top">
              <button className="mobile-back-btn" onClick={handleBackToHome}>
                ← Back
              </button>
              <div className="mobile-room-info">
                <span className="mobile-room-id">vs AI ({difficulty})</span>
              </div>
              <button className="mobile-reset-btn" onClick={resetGame}>
                🔄
              </button>
            </div>
            
            <div className="mobile-game-status" style={{
              backgroundColor: gameStatus.isGameOver 
                ? (gameStatus.winner === playerColor ? '#4ecdc4' : gameStatus.winner ? '#ff6b6b' : '#ffd700')
                : currentTurn === playerColor ? '#4ecdc4' : '#ffaa00'
            }}>
              <span className="mobile-status-text">
                {gameStatus.isGameOver
                  ? gameStatus.winner === playerColor ? '🎉 You Won!' 
                    : gameStatus.winner ? '🤖 AI Won!' 
                    : '🤝 Draw'
                  : currentTurn === playerColor 
                    ? isAiThinking ? '🤖 AI Thinking...' : '🎯 Your Turn'
                    : '🤖 AI Turn'
                }
              </span>
            </div>
          </div>
        )}

        {!isMobile && (
          <div className="game-header">
            <button className="back-button" onClick={handleBackToHome}>
              ← Back to Home
            </button>
            <div className="room-info">
              <h2>Single Player vs AI ({difficulty})</h2>
              <div className="connection-status">
                <span className="status-indicator connected"></span>
                {isAiThinking ? 'AI Thinking...' : 'Ready'}
              </div>
            </div>
            <button className="secondary-button" onClick={resetGame}>
              🔄 New Game
            </button>
          </div>
        )}

        <div className="game-content">
          <div className="game-main">
            <CaroBoard
              gameState={gameState}
              playerColor={playerColor}
              currentTurn={currentTurn}
              onMove={makeMove}
              gameStatus={gameStatus}
              boardSize={boardSize}
              winCondition={winCondition}
              lastMove={lastMove}
            />
          </div>

          <div className="game-sidebar">
            <h3>Game Info</h3>
            
            <div className="info-item">
              <span className="label">Mode:</span>
              <span className="value">Single Player</span>
            </div>
            
            <div className="info-item">
              <span className="label">Difficulty:</span>
              <span className="value">{difficulty}</span>
            </div>
            
            <div className="info-item">
              <span className="label">Board:</span>
              <span className="value">{boardSize}×{boardSize}</span>
            </div>
            
            <div className="info-item">
              <span className="label">Win Condition:</span>
              <span className="value">{winCondition} in a row</span>
            </div>

            <div className="player-info">
              <div className="player-header">
                <span className={`piece piece-${playerColor.toLowerCase()}`}>{playerColor}</span>
                <span className="player-name">You</span>
                <span className="you-indicator">(Human)</span>
              </div>
            </div>

            <div className={`player-info ${currentTurn === aiColor && !gameStatus.isGameOver ? 'active' : ''}`}>
              <div className="player-header">
                <span className={`piece piece-${aiColor.toLowerCase()}`}>{aiColor}</span>
                <span className="player-name">AI</span>
                <span className="you-indicator">({difficulty})</span>
              </div>
              {isAiThinking && (
                <div className="ai-thinking">
                  <div className="loading"></div>
                  <span>Thinking...</span>
                </div>
              )}
            </div>

            <div className="status-text">
              {gameStatus.isGameOver
                ? gameStatus.winner === playerColor ? 'You Win! 🎉'
                  : gameStatus.winner ? 'AI Wins! 🤖'
                  : 'Draw! 🤝'
                : currentTurn === playerColor
                  ? 'Your Turn'
                  : isAiThinking ? 'AI Thinking...' : 'AI Turn'
              }
            </div>

            {gameStats.gamesPlayed > 0 && (
              <div className="game-statistics">
                <div className="stats-header">
                  <h4>Your Statistics</h4>
                  <button 
                    className="reset-stats-btn" 
                    onClick={resetStats}
                    title="Reset Statistics"
                  >
                    🗑️
                  </button>
                </div>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-value">{gameStats.gamesPlayed}</span>
                    <span className="stat-label">Games</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{gameStats.wins}</span>
                    <span className="stat-label">Wins</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{gameStats.losses}</span>
                    <span className="stat-label">Losses</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{gameStats.draws}</span>
                    <span className="stat-label">Draws</span>
                  </div>
                  <div className="stat-item win-rate">
                    <span className="stat-value">
                      {gameStats.gamesPlayed > 0 ? Math.round((gameStats.wins / gameStats.gamesPlayed) * 100) : 0}%
                    </span>
                    <span className="stat-label">Win Rate</span>
                  </div>
                </div>
              </div>
            )}

            {gameStatus.isGameOver && (
              <div className="game-actions">
                <button className="primary-button" onClick={resetGame}>
                  🔄 New Game
                </button>
                <button className="secondary-button" onClick={handleBackToHome}>
                  ← Back to Home
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SinglePlayerGame;
